import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LOADING_STATUS, ROLES } from "../../common/constants";
import {
  checkRole,
  getUserClaimsAsync,
  selectAuthClaims,
  selectAuthStatus,
  selectIdentityClaims,
  selectLoginFirstName,
  selectLogoutUrl,
  selectOwnerAccountId,
  selectOwnerNumber,
} from "./authSlice";

export const useAuthUser = () => {
  const { NODE_ENV } = process.env;

  const dispatch = useAppDispatch();
  const identityClaims = useAppSelector(selectIdentityClaims);
  const authClaims = useAppSelector(selectAuthClaims);
  const authStatus = useAppSelector(selectAuthStatus);
  const hasValidRole = useAppSelector(
    checkRole([ROLES.OwnerMaintainerApplication]),
  );
  const isOwner = useAppSelector(checkRole([ROLES.Owner]));
  const isCleaner = useAppSelector(checkRole([ROLES.Cleaner]));
  const isKeymaintainer = useAppSelector(checkRole([ROLES.KeyMaintainer]));
  const isTechnicalMaintainer = useAppSelector(
    checkRole([ROLES.TechnicalMaintainer]),
  );
  const isLinenMaintainer = useAppSelector(checkRole([ROLES.LinenMaintainer]));

  const { firstName, lastName } = useAppSelector(selectLoginFirstName);
  const accountId = useAppSelector(selectOwnerAccountId);
  const ownerNumber = useAppSelector(selectOwnerNumber);
  const logoutUrl = useAppSelector(selectLogoutUrl);

  const isLoggedIn = identityClaims && authStatus === LOADING_STATUS.IDLE;

  useEffect(() => {
    if (!identityClaims) {
      dispatch(getUserClaimsAsync());
    }
  }, [dispatch, identityClaims]);

  useEffect(() => {
    if (
      authStatus === LOADING_STATUS.FAILED &&
      !identityClaims &&
      window.location.pathname !== "/"
    ) {
      // Store the current location path in localStorage for redirect after login
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
    }
  }, [authStatus, identityClaims, dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      const initKey = "accountSelectionInit";
      const hasInit = localStorage.getItem(initKey) === "true";
      if (!hasInit) {
        localStorage.removeItem("accountSelectionDone");
        localStorage.setItem(initKey, "true");
      }
    }
  }, [isLoggedIn]);

  const loginUrl =
    NODE_ENV === "production"
      ? "/bff/login?returnUrl=/"
      : "https://localhost:7040/bff/login?returnUrl=";

  const fullLogoutUrl =
    NODE_ENV === "production"
      ? logoutUrl
      : `https://localhost:7040${logoutUrl}`;

  return {
    loginFirstName: firstName,
    loginLastName: lastName,
    isLoggedIn,
    hasValidRole,
    isLoading: authStatus === LOADING_STATUS.LOADING,
    loginUrl,
    logoutUrl: fullLogoutUrl,
    ownerAccountId: accountId,
    isOwnerUser: isOwner,
    ownerNumber: ownerNumber,
  };
};
